import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { PageLoader } from '../components/ui/PageLoader';
import { PublicLayout } from '../components/layout/PublicLayout';
import { AppLayout } from '../components/layout/AppLayout';
import { ProtectedRoute, PublicOnlyRoute } from './ProtectedRoute';
import { registerUnauthorizedHandler } from '../api/client';
import { useAuthStore } from '../store/auth.store';

import { LoginPage } from '../pages/auth/LoginPage';
import { SignupPage } from '../pages/auth/SignupPage';
import { RestorePage } from '../pages/auth/RestorePage';
import { RestoreConfirmPage } from '../pages/auth/RestoreConfirmPage';
import { EmailConfirmedPage } from '../pages/auth/EmailConfirmedPage';

import { DashboardPage } from '../pages/app/DashboardPage';
import { FilesPage } from '../pages/app/FilesPage';
import { FileDetailsPage } from '../pages/app/FileDetailsPage';
import { TariffsPage } from '../pages/app/TariffsPage';
import { ProfilePage } from '../pages/app/ProfilePage';
import { ToolsIndexPage } from '../pages/app/tools/ToolsIndexPage';

import { NotFoundPage } from '../pages/NotFoundPage';

// Lazy: PDF tools pull in pdfjs-dist and @dnd-kit (§15).
const CompressPdfPage = lazy(() =>
  import('../pages/app/tools/CompressPdfPage').then((m) => ({ default: m.CompressPdfPage })),
);
const SplitPdfPage = lazy(() =>
  import('../pages/app/tools/SplitPdfPage').then((m) => ({ default: m.SplitPdfPage })),
);
const MergePdfPage = lazy(() =>
  import('../pages/app/tools/MergePdfPage').then((m) => ({ default: m.MergePdfPage })),
);

export function AppRouter() {
  const navigate = useNavigate();
  const hydrate = useAuthStore((s) => s.hydrate);
  const clearSession = useAuthStore((s) => s.clearSession);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      clearSession();
      navigate('/login', { replace: true });
    });
  }, [clearSession, navigate]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public / auth routes */}
        <Route
          element={
            <PublicOnlyRoute>
              <PublicLayoutWrapper />
            </PublicOnlyRoute>
          }
        >
          <Route path="login" element={<LoginPage />} />
          <Route path="signup" element={<SignupPage />} />
          <Route path="restore" element={<RestorePage />} />
          <Route path="restore/confirm" element={<RestoreConfirmPage />} />
        </Route>

        {/* Email confirmation is reachable whether logged in or not. */}
        <Route
          path="email/confirmed"
          element={
            <PublicLayout>
              <EmailConfirmedPage />
            </PublicLayout>
          }
        />

        {/* Protected app routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="files" element={<FilesPage />} />
          <Route path="files/:fileGUID" element={<FileDetailsPage />} />
          <Route path="tools" element={<ToolsIndexPage />} />
          <Route path="tools/compress" element={<CompressPdfPage />} />
          <Route path="tools/split" element={<SplitPdfPage />} />
          <Route path="tools/merge" element={<MergePdfPage />} />
          <Route path="tariffs" element={<TariffsPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>

        <Route path="" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="404"
          element={
            <PublicLayout>
              <NotFoundPage />
            </PublicLayout>
          }
        />
        <Route
          path="*"
          element={
            <PublicLayout>
              <NotFoundPage />
            </PublicLayout>
          }
        />
      </Routes>
    </Suspense>
  );
}

function PublicLayoutWrapper() {
  return (
    <PublicLayout>
      <Outlet />
    </PublicLayout>
  );
}
